/**
 * "תו נכה" check — public lookup card on the home screen + result modal.
 *
 * The card sits below the AI diagnostic hero and looks visually similar
 * (full-width gradient, prominent), but with an inline plate input
 * instead of a single click-target. Result modal shows the formatted
 * plate, a big bold yes/no answer, and the vehicle make/model/year/color
 * when available from the gov vehicles dataset.
 *
 * Public data only — no auth required. The check itself reuses
 * `hasDisabledTag()` from the CKAN client.
 */

import { hasDisabledTag, findVehicle } from '../../data/apis/ckan.js';
import { normalize as normalizePlate, isValid as isValidPlate, format as formatPlate } from '../../shared/plate.js';
import { escape } from '../../shared/escape.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('disabledBadge');

const CARD_ID = 'badgeCheckCard';
const INPUT_ID = 'badgeCheckInput';
const BUTTON_ID = 'badgeCheckBtn';
const ERROR_ID = 'badgeCheckErr';
const OVERLAY_ID = 'badgeCheckOverlay';

// ====================================================================
// Public API
// ====================================================================

/**
 * Build the home-screen hero card. Caller appends the returned element
 * into the home feed (homeView.js does this right after the AI hero).
 *
 * @returns {HTMLElement}
 */
export function renderCard() {
  ensureCardStyles();

  const el = document.createElement('div');
  el.id = CARD_ID;
  el.className = 'badge-hero b-row-full';
  el.innerHTML = `
    <div class="badge-hero-top">
      <div class="badge-hero-icon" aria-hidden="true">♿</div>
      <div class="badge-hero-body">
        <div class="badge-hero-title">בדיקת תו נכה לרכב</div>
        <div class="badge-hero-sub">האם הרכב מאושר לחניית נכים?</div>
      </div>
    </div>
    <div class="badge-hero-form">
      <input
        id="${INPUT_ID}"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        autocomplete="off"
        maxlength="10"
        placeholder="מספר רכב"
        aria-label="מספר רכב לבדיקה"
        dir="ltr"
      />
      <button id="${BUTTON_ID}" type="button" disabled>בדוק</button>
    </div>
    <div id="${ERROR_ID}" class="badge-hero-err" role="alert" aria-live="polite"></div>
  `;

  const input = /** @type {HTMLInputElement} */ (el.querySelector('#' + INPUT_ID));
  const button = /** @type {HTMLButtonElement} */ (el.querySelector('#' + BUTTON_ID));
  const errEl = /** @type {HTMLElement} */ (el.querySelector('#' + ERROR_ID));

  // Live formatting: every keystroke normalizes to digits-only, re-formats
  // with the canonical XX-XXX-XX (7) or XXX-XX-XXX (8) dashes, and toggles
  // the submit button's disabled state based on validity.
  input.addEventListener('input', () => {
    const digits = normalizePlate(input.value);
    const formatted = digits.length >= 4 ? formatPlate(digits) : digits;
    if (formatted !== input.value) {
      input.value = formatted;
    }
    button.disabled = !isValidPlate(digits);
    if (errEl.textContent) errEl.textContent = '';
  });

  // Enter on input = click submit (only if currently valid)
  input.addEventListener('keydown', (e) => {
    const ke = /** @type {KeyboardEvent} */ (e);
    if (ke.key === 'Enter' && !button.disabled) {
      ke.preventDefault();
      button.click();
    }
  });

  button.addEventListener('click', () => {
    const digits = normalizePlate(input.value);
    if (!isValidPlate(digits)) {
      errEl.textContent = 'יש להזין 7 או 8 ספרות.';
      return;
    }
    errEl.textContent = '';
    void openModal(digits);
  });

  return el;
}

/**
 * Open the result modal for a given plate. Performs the disabled-tag
 * check + a parallel vehicle-details lookup, then renders both.
 *
 * @param {string} rawPlate
 */
export async function openModal(rawPlate) {
  const digits = normalizePlate(rawPlate);
  if (!isValidPlate(digits)) {
    log.warn('openModal called with invalid plate:', rawPlate);
    return;
  }

  ensureModalStyles();
  const overlay = ensureModal();
  document.body.appendChild(overlay); // re-append so z-index ties resolve in our favor
  const body = /** @type {HTMLElement} */ (overlay.querySelector('#badgeCheckBody'));

  // Loading state — show plate + spinner immediately so the user has
  // visual feedback while the two CKAN calls run.
  body.innerHTML = `
    <div class="badge-modal-loading">
      <div class="badge-modal-plate-wrap">
        <span class="badge-modal-plate-label">מספר רכב</span>
        <span class="badge-modal-plate">${escape(formatPlate(digits))}</span>
      </div>
      <div class="badge-modal-spinner" aria-hidden="true"></div>
      <div class="badge-modal-loading-text">בודק במאגר משרד התחבורה...</div>
    </div>
  `;
  overlay.classList.add('open');

  // Both lookups in parallel. Each is wrapped in Promise.resolve().catch
  // so neither one's failure prevents the other from showing.
  const [tagResult, vehicleResult] = await Promise.allSettled([
    hasDisabledTag(digits),
    findVehicle(digits),
  ]);

  const hasTag = tagResult.status === 'fulfilled' ? tagResult.value : null;
  const vehicleRecord =
    vehicleResult.status === 'fulfilled' && vehicleResult.value.records.length > 0
      ? vehicleResult.value.records[0]
      : null;

  body.innerHTML = renderResult({
    plate: digits,
    hasTag,
    vehicle: vehicleRecord,
    tagError: tagResult.status === 'rejected',
  });
}

/** Close the result modal. */
export function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) overlay.classList.remove('open');
}

// ====================================================================
// Internals — modal DOM + result rendering
// ====================================================================

function ensureModal() {
  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) return overlay;
  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'badge-modal-overlay';
  overlay.innerHTML = `
    <aside class="badge-modal" role="dialog" aria-modal="true" aria-labelledby="badgeModalTitle">
      <header class="badge-modal-header">
        <div id="badgeModalTitle" class="badge-modal-title">בדיקת תו נכה</div>
        <button class="badge-modal-close" id="badgeModalCloseBtn" type="button" aria-label="סגור">✕</button>
      </header>
      <div id="badgeCheckBody" class="badge-modal-body"></div>
      <footer class="badge-modal-footer">
        <button class="badge-modal-ok" id="badgeModalOkBtn" type="button">סגור</button>
      </footer>
    </aside>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('#badgeModalCloseBtn')?.addEventListener('click', close);
  overlay.querySelector('#badgeModalOkBtn')?.addEventListener('click', close);
  // Esc closes too.
  document.addEventListener('keydown', (e) => {
    const ke = /** @type {KeyboardEvent} */ (e);
    if (ke.key === 'Escape' && overlay?.classList.contains('open')) close();
  });
  return overlay;
}

/**
 * @param {Object} args
 * @param {string} args.plate     digits only
 * @param {boolean | null} args.hasTag   null = lookup failed
 * @param {any} args.vehicle             gov record (may be null if not found)
 * @param {boolean} args.tagError        true if hasDisabledTag rejected
 */
function renderResult({ plate, hasTag, vehicle, tagError }) {
  const plateHtml = `
    <div class="badge-modal-plate-wrap">
      <span class="badge-modal-plate-label">מספר רכב</span>
      <span class="badge-modal-plate">${escape(formatPlate(plate))}</span>
    </div>
  `;

  // Result banner — biggest visual element. Three states:
  //   1. Tag lookup failed                → yellow warning
  //   2. Tag lookup succeeded, no record  → neutral gray ("לא רשום")
  //   3. Tag lookup succeeded, has record → green ("מאושר")
  let banner;
  if (tagError || hasTag === null) {
    banner = `
      <div class="badge-result badge-result-unknown">
        <div class="badge-result-icon" aria-hidden="true">⚠️</div>
        <div class="badge-result-headline">לא הצלחנו לבדוק כרגע</div>
        <div class="badge-result-sub">המאגר אינו זמין כרגע. נסה שוב בעוד רגע.</div>
      </div>
    `;
  } else if (hasTag) {
    banner = `
      <div class="badge-result badge-result-yes">
        <div class="badge-result-icon" aria-hidden="true">✅</div>
        <div class="badge-result-headline">מאושר לתו נכה</div>
        <div class="badge-result-sub">הרכב רשום כתו נכה תקין במאגר משרד התחבורה.</div>
      </div>
    `;
  } else {
    banner = `
      <div class="badge-result badge-result-no">
        <div class="badge-result-icon" aria-hidden="true">❌</div>
        <div class="badge-result-headline">לא רשום כתו נכה</div>
        <div class="badge-result-sub">הרכב אינו מופיע במאגר תווי הנכה של משרד התחבורה.</div>
      </div>
    `;
  }

  // Vehicle details — only shown if we got something from CKAN
  let detailsHtml = '';
  if (vehicle) {
    const manufacturer = String(vehicle.tozeret_nm || '').trim();
    const commercial = String(vehicle.kinuy_mishari || vehicle.degem_nm || '').trim();
    const fullModel = [manufacturer, commercial].filter(Boolean).join(' ');
    const year = vehicle.shnat_yitzur || '';
    const color = String(vehicle.tzeva_rechev || '').trim();
    const rows = [];
    if (fullModel) rows.push(['דגם', fullModel]);
    if (year) rows.push(['שנת ייצור', String(year)]);
    if (color) rows.push(['צבע', color]);
    if (rows.length > 0) {
      detailsHtml = `
        <div class="badge-modal-details">
          <div class="badge-modal-details-title">📋 פרטי הרכב</div>
          <dl class="badge-modal-details-list">
            ${rows.map(([k, v]) => `
              <div class="badge-modal-details-row">
                <dt>${escape(k)}</dt>
                <dd>${escape(v)}</dd>
              </div>
            `).join('')}
          </dl>
        </div>
      `;
    }
  }

  return plateHtml + banner + detailsHtml;
}

// ====================================================================
// Styles — injected once, kept inline so the feature is self-contained
// ====================================================================

let __cardStylesInjected = false;
function ensureCardStyles() {
  if (__cardStylesInjected) return;
  __cardStylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    /* Brand-teal gradient hero — distinct from the indigo AI diagnostic
       hero above. Uses the AutoMe brand color (#2DA88A) family so the
       two heroes read as sibling features without competing. */
    .badge-hero {
      position: relative;
      padding: 16px 16px 18px;
      background: linear-gradient(135deg, #2DA88A 0%, #258A72 50%, #1C6E5B 100%);
      color: #fff;
      border-radius: 18px;
      overflow: hidden;
      box-shadow: 0 8px 24px rgba(45, 168, 138, 0.30);
    }
    .badge-hero::before {
      content: '';
      position: absolute;
      top: -20px; right: -20px;
      width: 120px; height: 120px;
      background: radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%);
      pointer-events: none;
    }
    .badge-hero-top {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 14px;
    }
    .badge-hero-icon {
      flex-shrink: 0;
      width: 48px; height: 48px;
      background: rgba(255,255,255,0.22);
      border-radius: 13px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 26px;
    }
    .badge-hero-body { flex: 1; min-width: 0; }
    .badge-hero-title {
      font-family: 'Rubik', sans-serif;
      font-weight: 900;
      font-size: 16px;
      margin-bottom: 3px;
      letter-spacing: -0.01em;
    }
    .badge-hero-sub {
      font-size: 12px;
      opacity: 0.92;
      line-height: 1.4;
    }
    .badge-hero-form {
      display: flex;
      gap: 8px;
      align-items: stretch;
    }
    .badge-hero-form input {
      flex: 1;
      min-width: 0;
      padding: 11px 14px;
      border: none;
      border-radius: 12px;
      background: rgba(255,255,255,0.96);
      color: #0f172a;
      font-family: 'Rubik', sans-serif;
      font-size: 16px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-align: center;
      outline: none;
      box-shadow: 0 2px 6px rgba(0,0,0,0.12) inset;
    }
    .badge-hero-form input::placeholder {
      color: #94a3b8;
      font-weight: 500;
      letter-spacing: 0;
    }
    .badge-hero-form input:focus {
      box-shadow: 0 0 0 3px rgba(255,255,255,0.45);
    }
    .badge-hero-form button {
      flex-shrink: 0;
      padding: 0 18px;
      border: none;
      border-radius: 12px;
      background: rgba(255,255,255,0.96);
      color: #1C6E5B;
      font-family: 'Rubik', sans-serif;
      font-weight: 900;
      font-size: 14px;
      cursor: pointer;
      transition: transform 0.12s, opacity 0.18s;
    }
    .badge-hero-form button:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
    .badge-hero-form button:not(:disabled):hover {
      transform: translateY(-1px);
    }
    .badge-hero-err {
      min-height: 0;
      margin-top: 6px;
      font-size: 12px;
      color: #fef3c7;
      font-weight: 600;
    }
    .badge-hero-err:empty { margin-top: 0; }
  `;
  document.head.appendChild(style);
}

let __modalStylesInjected = false;
function ensureModalStyles() {
  if (__modalStylesInjected) return;
  __modalStylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .badge-modal-overlay {
      position: fixed; inset: 0;
      background: rgba(15, 23, 42, 0.55);
      backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
      z-index: 99998;
      display: none;
      align-items: center; justify-content: center;
      padding: 20px;
      font-family: 'Rubik', 'Heebo', sans-serif;
    }
    .badge-modal-overlay.open { display: flex; }
    .badge-modal {
      background: #fff;
      border-radius: 20px;
      width: 100%;
      max-width: 440px;
      max-height: 90vh;
      display: flex;
      flex-direction: column;
      direction: rtl;
      overflow: hidden;
      box-shadow: 0 30px 80px rgba(0,0,0,0.40);
      animation: badge-modal-pop 0.22s ease-out;
    }
    @keyframes badge-modal-pop {
      from { transform: scale(0.94); opacity: 0; }
      to   { transform: scale(1);    opacity: 1; }
    }
    .badge-modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 18px;
      border-bottom: 1px solid #e2e8f0;
    }
    .badge-modal-title {
      font-weight: 800;
      font-size: 16px;
      color: #0f172a;
    }
    .badge-modal-close {
      background: transparent;
      border: none;
      font-size: 22px;
      color: #64748b;
      cursor: pointer;
      line-height: 1;
      padding: 4px 8px;
    }
    .badge-modal-close:hover { color: #0f172a; }
    .badge-modal-body {
      padding: 18px;
      overflow-y: auto;
      flex: 1;
    }
    .badge-modal-footer {
      padding: 12px 18px 16px;
      border-top: 1px solid #e2e8f0;
      display: flex;
      justify-content: center;
    }
    .badge-modal-ok {
      padding: 11px 36px;
      border: none;
      border-radius: 999px;
      background: linear-gradient(135deg, #2DA88A, #258A72);
      color: #fff;
      font-family: inherit;
      font-weight: 800;
      font-size: 14px;
      cursor: pointer;
      box-shadow: 0 6px 16px rgba(45,168,138,0.30);
    }
    .badge-modal-plate-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      margin-bottom: 18px;
    }
    .badge-modal-plate-label {
      font-size: 11px;
      color: #64748b;
      font-weight: 600;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }
    .badge-modal-plate {
      display: inline-block;
      background: #fef3c7;
      color: #0f172a;
      padding: 8px 16px;
      border-radius: 8px;
      font-family: 'Courier New', monospace;
      font-weight: 900;
      font-size: 22px;
      letter-spacing: 0.08em;
      border: 2px solid #f59e0b;
      direction: ltr;
    }
    .badge-modal-loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
      padding: 20px 0 30px;
    }
    .badge-modal-spinner {
      width: 34px; height: 34px;
      border: 3px solid #e2e8f0;
      border-top-color: #2DA88A;
      border-radius: 50%;
      animation: badge-spin 0.8s linear infinite;
    }
    @keyframes badge-spin {
      to { transform: rotate(360deg); }
    }
    .badge-modal-loading-text {
      color: #64748b;
      font-size: 13px;
    }
    /* Result banner — the headline of the modal */
    .badge-result {
      padding: 22px 18px;
      border-radius: 14px;
      text-align: center;
      margin-bottom: 18px;
    }
    .badge-result-icon {
      font-size: 38px;
      margin-bottom: 8px;
      line-height: 1;
    }
    .badge-result-headline {
      font-weight: 900;
      font-size: 22px;
      margin-bottom: 6px;
      letter-spacing: -0.01em;
    }
    .badge-result-sub {
      font-size: 13px;
      line-height: 1.5;
      opacity: 0.85;
    }
    .badge-result-yes {
      background: linear-gradient(135deg, #d1fae5, #a7f3d0);
      color: #065f46;
    }
    .badge-result-no {
      background: linear-gradient(135deg, #fee2e2, #fecaca);
      color: #991b1b;
    }
    .badge-result-unknown {
      background: linear-gradient(135deg, #fef3c7, #fde68a);
      color: #78350f;
    }
    /* Vehicle details section */
    .badge-modal-details {
      background: #f8fafc;
      border-radius: 12px;
      padding: 14px 16px;
    }
    .badge-modal-details-title {
      font-weight: 800;
      font-size: 13px;
      color: #475569;
      margin-bottom: 10px;
    }
    .badge-modal-details-list {
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .badge-modal-details-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 13px;
    }
    .badge-modal-details-row dt {
      color: #64748b;
      font-weight: 600;
      margin: 0;
    }
    .badge-modal-details-row dd {
      color: #0f172a;
      font-weight: 700;
      margin: 0;
    }
  `;
  document.head.appendChild(style);
}
